/**
 * staff-notice — what a staff member acknowledges before the lists: Part B of
 * the staff notice (acknowledgment and confidentiality) with one checkbox,
 * and Part A (how the backoffice processes their data) a press away, in its
 * own overlay, so the screen never grows to hold it. The viewport-bound
 * scroll region is only for a screen too short for Part B itself, where it
 * keeps Continue reachable.
 */
import { fill, gravity, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { session } from "@momo/views";
import { html } from "lit";
import { state } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import { de, en } from "./staff-notice.text.ts";
import { acknowledgeNotice$, noticeAct, noticeRetry$, staffNotice } from "./staff.state.ts";

@schmancyElement("staff-notice")
export class StaffNoticeView extends LocalizeMixin(SchmancyElement) {
	@state() private checked = false;

	override render() {
		const text = this.uiLanguage === "de" ? de : en;
		const notice = staffNotice.value;
		const act = noticeAct.value;
		return html`
			<schmancy-surface type="containerLowest" opacity="solid" ${fill()} ${overflowWithin()} class="w-full" tabindex="0" role="region" aria-label=${text.title}>
				<div class="mx-auto flex w-full max-w-2xl flex-col py-8 sm:py-12">
					${notice === "unanswered"
						? html`
								<div class="flex min-h-[28rem] flex-col items-center justify-center gap-4 p-6">
									<schmancy-spinner size="lg" aria-hidden="true"></schmancy-spinner>
									<schmancy-visually-hidden role="status" aria-live="polite">${this.t("backoffice.notice.loading")}</schmancy-visually-hidden>
								</div>
							`
						: notice === "error"
							? html`
									<div class="flex flex-col gap-4 px-4 py-6 sm:px-6">
										<schmancy-typography role="alert" type="body" token="md" class="text-error-default">${this.t("backoffice.notice.failed")}</schmancy-typography>
											<schmancy-button variant="filled" class="self-start" size="md" @click=${() => noticeRetry$.next()}>
											${this.t("backoffice.retry")}
										</schmancy-button>
									</div>
								`
							: html`
									<div class="flex flex-col gap-6 px-4 py-6 sm:px-6">
										<div ${gravity({ delay: 0, distance: 20 })} class="flex flex-col gap-1">
											<schmancy-typography type="headline" token="sm">${text.partB.title}</schmancy-typography>
											<schmancy-typography type="body" token="md">
												${this.t("backoffice.notice.signingInAs")} <strong>${session.value.email}</strong>.
											</schmancy-typography>
										</div>

										<div ${gravity({ delay: 30, distance: 16 })} class="flex flex-col gap-2">
											<schmancy-typography type="body" token="sm">${text.partB.intro}</schmancy-typography>
											<ol class="grid list-decimal gap-3 pl-5">
												${repeat(
													text.partB.items,
													(item) => item,
													(item) => html`<li><schmancy-typography type="body" token="sm">${item}</schmancy-typography></li>`,
												)}
											</ol>
										</div>

										<div ${gravity({ delay: 50, distance: 14 })} class="flex flex-col gap-3">
											<schmancy-checkbox
												.value=${this.checked}
												?disabled=${act === "busy"}
												@change=${(e: CustomEvent<{ value: boolean }>) => {
													if (act !== "busy") this.checked = e.detail?.value ?? false;
												}}
											>
												<schmancy-typography type="body" token="sm">${text.checkbox}</schmancy-typography>
											</schmancy-checkbox>
											<schmancy-button
												variant="text"
												size="md"
												class="self-start"
												@click=${() =>
													show(
														() => html`
															<div class="grid max-w-2xl gap-4 p-6">
																<schmancy-typography type="label" token="md">${text.version}</schmancy-typography>
																${repeat(
																	text.partA.sections,
																	(section) => section.heading,
																	(section) => html`
																		<section class="grid gap-2">
																			<schmancy-typography type="title" token="sm">${section.heading}</schmancy-typography>
																			${repeat(
																				section.blocks,
																				(_block, i) => i,
																				(block) =>
																					"items" in block
																						? html`<ul class="grid list-disc gap-1 pl-5">
																								${repeat(block.items, (item) => item, (item) => html`<li><schmancy-typography type="body" token="sm">${item}</schmancy-typography></li>`)}
																							</ul>`
																						: html`<schmancy-typography type="body" token="sm">
																								${block.lead ? html`<strong>${block.lead}</strong> ` : ""}${block.text}
																							</schmancy-typography>`,
																			)}
																		</section>
																	`,
																)}
															</div>
														`,
														{ ariaLabel: text.partA.title },
													).subscribe()}
											>
												${text.partA.title}
											</schmancy-button>
										</div>

										<div class="grid" aria-live="polite">
											<schmancy-surface type="error" rounded="all" class="invisible col-start-1 row-start-1 rounded-xl px-4 py-3" aria-hidden="true">
												<schmancy-typography type="body" token="sm">${this.t("backoffice.notice.recordFailed")}</schmancy-typography>
											</schmancy-surface>
											<schmancy-surface
												type="error"
												rounded="all"
												class=${this.classMap({ "col-start-1 row-start-1 rounded-xl px-4 py-3": true, invisible: act !== "failed" })}
												aria-hidden=${act !== "failed" ? "true" : "false"}
											>
												<schmancy-typography type="body" token="sm" role="alert">${act === "failed" ? this.t("backoffice.notice.recordFailed") : ""}</schmancy-typography>
											</schmancy-surface>
										</div>

										<div ${gravity({ delay: 100, distance: 12 })}>
											<schmancy-button
												variant="filled"
												class="self-start"
												size="md"
												?disabled=${!this.checked}
												.busy=${act === "busy"}
												@click=${() => {
													if (this.checked && act !== "busy") acknowledgeNotice$.next();
												}}
											>
												${this.t("backoffice.notice.continue")}
											</schmancy-button>
										</div>
									</div>
								`}
				</div>
			</schmancy-surface>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"staff-notice": StaffNoticeView;
	}
}
